import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Download,
  ExternalLink,
  FileText,
  Loader2,
} from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LabelList,
} from "recharts";
import { api, pdfUrl } from "../lib/api";
import { ChamadasExtrasSection } from "./ChamadasExtrasSection";
import { CotasSection } from "./CotasSection";
import { DespesasCategoriaSection, DespesasFundoReservaSection } from "./DespesasCategoriaSection";
import { DocumentosFiscaisSection } from "./DocumentosFiscaisSection";
import { ChipPagina, DigitarPaginas } from "./PaginaUI";
import { ResumoDocsSection } from "./ResumoDocsSection";
import { VisualizadorPaginas } from "./VisualizadorPaginas";

interface Props {
  competenciaId: number;
  /** Seção para a qual rolar ao abrir (vinda dos cards do dashboard). */
  secao?: string;
  onVoltar: () => void;
  /** Abre o Controle de Rateio do gás no mês indicado (uso opcional, a partir
   *  do alerta de divergência no Resumo de DOC's). */
  onAbrirGasRateio?: (mesReferencia: string) => void;
}

// Mapeia a chave do card do dashboard -> id da seção nesta página.
const SECAO_ANCORA: Record<string, string> = {
  tendencia: "sec-tendencia",
  saldos: "sec-saldos",
  cotas: "sec-cotas",
  "chamadas-extras": "sec-chamadas-extras",
  docs: "sec-docs",
  despesas: "sec-despesas",
  fiscal: "sec-fiscal",
  findings: "sec-findings",
};

interface TendenciaMes {
  competencia_id: number;
  ano: number;
  mes: number;
  label: string;
  unc_receita: number; unc_despesa: number; unc_resultado: number; unc_saldo: number;
  fre_receita: number; fre_despesa: number; fre_resultado: number; fre_saldo: number;
}
interface TendenciaResp {
  condominio_id: number;
  meses: TendenciaMes[];
}

// Formata número (não string) em R$ compacto para eixos/tooltip dos gráficos.
function fmtBRLnum(n: number, compacto = false): string {
  if (compacto && Math.abs(n) >= 1000) {
    return "R$ " + (n / 1000).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "k";
  }
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function TendenciaChart({
  titulo, dados, kUnc, kFre, totalLabel = "Total (UNC + FRE)",
}: {
  titulo: string;
  dados: TendenciaMes[];
  kUnc: keyof TendenciaMes;
  kFre: keyof TendenciaMes;
  totalLabel?: string;   // no gráfico Resultado vira "Diferença (Receita − Despesa)"
}) {
  // Linha-herói: soma das duas contas (UNC + FRE). No gráfico Resultado essa soma
  // é a diferença consolidada (Receita − Despesa) do mês.
  const linhas = dados.map((m) => ({
    ...m,
    _total: (Number(m[kUnc]) || 0) + (Number(m[kFre]) || 0),
  }));
  const uid = titulo.replace(/[^a-zA-Z0-9]/g, "").slice(0, 12) || "t";
  const ultimo = linhas.length - 1;

  // Rótulos só nas pontas (primeiro/último), como no modelo de referência.
  const PontaLabel = (props: {
    x?: number; y?: number; value?: number; index?: number;
  }): ReactElement | null => {
    const { x = 0, y = 0, value = 0, index = -1 } = props;
    if (index !== 0 && index !== ultimo) return null;
    const fim = index === ultimo;
    return (
      <text x={x} y={y - 10} textAnchor={fim ? "end" : "start"}
        fill={fim ? "#fca5a5" : "#cbd5e1"} fontSize={11} fontWeight={700}
        style={{ letterSpacing: "0.04em" }}>
        {fmtBRLnum(value, true)}
      </text>
    );
  };

  return (
    <div
      className="rounded-2xl p-4 border overflow-hidden relative"
      style={{
        background:
          "radial-gradient(90% 70% at 12% -5%, rgba(99,102,241,0.22), transparent 60%)," +
          "radial-gradient(85% 90% at 105% 115%, rgba(244,63,94,0.14), transparent 55%)," +
          "linear-gradient(160deg,#151b30 0%,#0c1122 58%,#0a0e1b 100%)",
        borderColor: "rgba(255,255,255,0.08)",
        boxShadow: "0 18px 46px rgba(6,10,24,0.55), inset 0 1px 0 rgba(255,255,255,0.06)",
      }}
    >
      <h4 className="text-[13px] font-semibold text-slate-100 mb-2 tracking-wide">{titulo}</h4>
      <div style={{ width: "100%", height: 236 }}>
        <ResponsiveContainer>
          <ComposedChart data={linhas} margin={{ top: 16, right: 18, left: 0, bottom: 4 }}>
            <defs>
              {/* Traço da linha-herói: frio (esq.) → quente (dir.), como na referência */}
              <linearGradient id={`stroke-${uid}`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#a5b4fc" />
                <stop offset="45%" stopColor="#fcd34d" />
                <stop offset="78%" stopColor="#fb923c" />
                <stop offset="100%" stopColor="#f43f5e" />
              </linearGradient>
              {/* Brilho/área luminosa por baixo da linha */}
              <linearGradient id={`fill-${uid}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fb923c" stopOpacity={0.30} />
                <stop offset="55%" stopColor="#fb923c" stopOpacity={0.08} />
                <stop offset="100%" stopColor="#fb923c" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 6" stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#8ea0bd" }}
              tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.10)" }} interval="preserveStartEnd" />
            <YAxis tickFormatter={(v) => fmtBRLnum(v, true)} tick={{ fontSize: 10, fill: "#8ea0bd" }}
              tickLine={false} axisLine={false} width={54} />
            <Tooltip formatter={(v: number) => fmtBRLnum(v)}
              contentStyle={{ background: "rgba(12,17,34,0.96)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 10, color: "#e2e8f0", boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}
              labelStyle={{ color: "#e2e8f0" }} itemStyle={{ color: "#e2e8f0" }} />
            {/* Legenda com cores sólidas (o degradê da linha-herói não aparece no
                quadradinho da legenda). A cor do "Total" representa a linha do gráfico. */}
            <Legend
              iconType="plainline"
              wrapperStyle={{ fontSize: "11px", color: "#cbd5e1" }}
              payload={[
                { value: "UNC (conta ordinária)", type: "plainline", id: "unc", color: "#7dd3fc", payload: { strokeDasharray: "0" } },
                { value: "FRE (fundo de reserva)", type: "plainline", id: "fre", color: "#94a3b8", payload: { strokeDasharray: "5 5" } },
                { value: totalLabel, type: "plainline", id: "total", color: "#fb923c", payload: { strokeDasharray: "0" } },
              ] as never}
            />
            {/* Brilho por baixo */}
            <Area type="monotone" dataKey="_total" legendType="none" tooltipType="none"
              stroke="none" fill={`url(#fill-${uid})`} isAnimationActive={false} />
            {/* Linhas de apoio, discretas */}
            <Line type="monotone" dataKey={kUnc as string} name="UNC (conta ordinária)"
              stroke="rgba(125,211,252,0.55)" strokeWidth={1.6} dot={false} activeDot={{ r: 4 }} />
            <Line type="monotone" dataKey={kFre as string} name="FRE (fundo de reserva)"
              stroke="rgba(148,163,184,0.5)" strokeWidth={1.4} strokeDasharray="5 5" dot={false} activeDot={{ r: 4 }} />
            {/* Linha-herói: traço em degradê + pontas destacadas */}
            <Line type="monotone" dataKey="_total" name={totalLabel}
              stroke={`url(#stroke-${uid})`} strokeWidth={3.2} strokeLinecap="round"
              dot={false} activeDot={{ r: 6, fill: "#f43f5e", stroke: "#fff", strokeWidth: 1 }}
              isAnimationActive={true}>
              <LabelList dataKey="_total" content={PontaLabel as never} />
            </Line>
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// =====================================================================
// Tipos do payload /api/competencias/{id}/detalhe
// =====================================================================
interface ContaLeve {
  id: number;
  codigo: string;
  nome: string;
}

interface ResumoContaDetalhe {
  id: number;
  saldo_anterior: string;
  receitas: string;
  despesas: string;
  saldo_final: string;
  fonte_pagina: string | null;
  confianca: string | null;
  conta: ContaLeve;
}

interface FindingOut {
  id: number;
  severidade: "PASS" | "WARN" | "FAIL";
  validacao: string;
  mensagem: string;
  valor_esperado: string | null;
  valor_encontrado: string | null;
  evidencia_pagina: string | null;
}

interface PdfOut {
  id: number;
  nome_arquivo: string;
  n_paginas: number | null;
}

interface CompetenciaDetalhe {
  id: number;
  condominio_id: number;
  condominio_nome: string;
  ano: number;
  mes: number;
  periodo_str: string;
  status: string;
  importado_em: string | null;
  auditado_em: string | null;
  observacao: string | null;
  pdfs: PdfOut[];
  resumos: ResumoContaDetalhe[];
  findings: FindingOut[];
  findings_pass: number;
  findings_warn: number;
  findings_fail: number;
  resumo_docs_texto: string | null;
  resumo_docs_pagina: string | null;
  resumo_despesas_json: string | null;
  documentos_fiscais_json: string | null;
  gas_auditoria: GasAuditoria | null;
}

interface GasAuditoriaUnidade {
  unidade: string;
  cobrado: number | null;
  devido: number | null;
  diferenca: number | null;
}
export interface GasAuditoria {
  mes_referencia: string;
  ciclo_id: number;
  aprovado: boolean;
  divergente: boolean;
  cobrado: number;
  devido: number;
  diferenca: number;
  diferenca_pct: number;
  tem_por_unidade: boolean;
  por_unidade: GasAuditoriaUnidade[];
}

const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

// Formata Decimal (vem como string do backend) em pt-BR
function fmtBRL(v: string | null | undefined): string {
  if (v == null) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return n.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

// Formata data ISO -> dd/mm/yyyy HH:MM
function fmtData(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Seta de retorno exibida no topo de cada relatório, para o usuário voltar
// à fileira de botões do dashboard sem precisar rolar a página inteira.
function VoltarRelatorios({ onVoltar }: { onVoltar: () => void }) {
  return (
    <button
      type="button"
      onClick={onVoltar}
      className="mb-2 inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800"
    >
      <ArrowLeft size={14} />
      Voltar aos relatórios
    </button>
  );
}

export function ResultadosPage({ competenciaId, secao, onVoltar, onAbrirGasRateio }: Props) {
  const { data, isLoading, error } = useQuery<CompetenciaDetalhe>({
    queryKey: ["competencia-detalhe", competenciaId],
    queryFn: () =>
      api.get<CompetenciaDetalhe>(`/competencias/${competenciaId}/detalhe`),
  });

  // Tendência 12 meses (UNC × FRE) — carrega quando já sabemos o condomínio.
  const { data: tendencia } = useQuery<TendenciaResp>({
    queryKey: ["tendencia", data?.condominio_id],
    queryFn: () =>
      api.get<TendenciaResp>(`/condominios/${data!.condominio_id}/tendencia-12m`),
    enabled: !!data?.condominio_id,
  });

  // Visualizador de páginas — alimentado pelas 3 portas (A digitar, B PDF, C clicar).
  const [paginasView, setPaginasView] = useState<number[] | null>(null);
  const abrirPagina = (p: number) => setPaginasView([p]);
  const abrirPaginas = (ps: number[]) => setPaginasView(ps);
  const fecharViewer = () => setPaginasView(null);

  // Rola até a seção pedida pelo card do dashboard, após os dados carregarem.
  useEffect(() => {
    if (!data || !secao) return;
    const id = SECAO_ANCORA[secao];
    if (!id) return;
    // pequeno atraso garante que as seções já renderizaram
    const t = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => window.clearTimeout(t);
  }, [data, secao]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-slate-600 text-sm">
        <Loader2 size={16} className="animate-spin" />
        Carregando resultados...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div>
        <button
          type="button"
          onClick={onVoltar}
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-4"
        >
          <ArrowLeft size={16} />
          Voltar
        </button>
        <div className="p-4 bg-red-50 border border-red-200 rounded-card text-sm text-red-800">
          Não foi possível carregar os resultados.{" "}
          {error instanceof Error ? error.message : ""}
        </div>
      </div>
    );
  }

  const periodoLabel = `${MESES[data.mes - 1]}/${data.ano}`;
  const totalPaginas =
    data.pdfs.reduce((acc, p) => acc + (p.n_paginas ?? 0), 0) || undefined;

  return (
    <div>
      {/* Visualizador de páginas (modal) */}
      <VisualizadorPaginas
        competenciaId={competenciaId}
        paginas={paginasView ?? []}
        aberto={paginasView != null}
        onFechar={fecharViewer}
      />
      {/* Cabeçalho + voltar */}
      <button
        type="button"
        onClick={onVoltar}
        className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-4"
      >
        <ArrowLeft size={16} />
        Voltar aos relatórios
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">
            Resultados — {periodoLabel}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {data.condominio_nome}
            {data.auditado_em && (
              <> · Auditado em {fmtData(data.auditado_em)}</>
            )}
          </p>
        </div>

        {/* Resumo de findings + botao debug */}
        <div className="flex gap-2 items-start">
          <BadgeContagem
            tipo="pass"
            valor={data.findings_pass}
            label="OK"
          />
          <BadgeContagem
            tipo="warn"
            valor={data.findings_warn}
            label="Atenção"
          />
          <BadgeContagem
            tipo="fail"
            valor={data.findings_fail}
            label="Falhas"
          />
          {/* Botao para baixar texto OCR (debug) — abre em nova aba */}
          <a
            href={`/api/competencias/${competenciaId}/ocr-texto-completo`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-slate-600 hover:text-slate-900 border border-slate-300 hover:border-slate-400 rounded-md"
            title="Abre texto OCR de todas as páginas em nova aba (para diagnóstico)"
          >
            <Download size={12} />
            Texto OCR
          </a>
        </div>
      </div>

      {/* Ver páginas do balancete: digitar (A) + abrir PDF/pasta (B) */}
      {data.pdfs.length > 0 && (
        <div className="mt-4 rounded-card border border-slate-200 bg-slate-50/60 px-4 py-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
              Ver páginas do balancete
            </span>

            {/* A) Digitar páginas (2;7;9 ou 5-9) */}
            <DigitarPaginas max={totalPaginas} onAbrir={abrirPaginas} />

            <span className="text-slate-300">|</span>

            {/* B) Abrir PDF original / abrir a pasta */}
            <a
              href={pdfUrl(competenciaId)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border border-slate-300 text-slate-700 hover:border-slate-400 hover:bg-white"
              title="Abrir o PDF original do balancete em nova aba"
            >
              <ExternalLink size={13} /> Abrir PDF original
            </a>
          </div>

          {/* Lista de PDFs (clicáveis) */}
          <div className="mt-2.5 flex flex-wrap gap-2">
            {data.pdfs.map((pdf) => (
              <a
                key={pdf.id}
                href={pdfUrl(competenciaId, undefined, pdf.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-slate-200 text-slate-700 text-xs rounded-md hover:border-indigo-300 hover:text-indigo-700"
                title={`Abrir ${pdf.nome_arquivo}`}
              >
                <FileText size={12} />
                {pdf.nome_arquivo}
                {pdf.n_paginas && (
                  <span className="text-slate-400">({pdf.n_paginas}p)</span>
                )}
              </a>
            ))}
          </div>

          <p className="mt-2 text-[11px] text-slate-500">
            Dica: digite páginas como <span className="font-mono">2;7;9</span>{" "}
            (avulsas) ou <span className="font-mono">5-9</span> (intervalo). Ou
            clique no <span className="text-indigo-600 font-medium">🔎 pág.</span>{" "}
            ao lado de cada linha para ir direto à página citada.
          </p>
        </div>
      )}

      {/* Tendência (12 meses) — UNC × FRE */}
      <section id="sec-tendencia" className="mt-8 scroll-mt-4">
        <VoltarRelatorios onVoltar={onVoltar} />
        <h3 className="text-lg font-semibold text-slate-900 mb-1">
          Tendência (12 meses)
        </h3>
        <p className="text-sm text-slate-500 mb-3">
          Evolução de receita, despesa, resultado e saldo — separando a conta
          ordinária (UNC) do fundo de reserva (FRE).
        </p>
        {!tendencia ? (
          <div className="text-sm text-slate-500 flex items-center gap-2 py-8">
            <Loader2 size={16} className="animate-spin" /> Carregando tendência…
          </div>
        ) : tendencia.meses.length === 0 ? (
          <div className="text-sm text-slate-500 py-8">
            Ainda não há meses processados para montar a tendência.
          </div>
        ) : (
          <>
            <div className="flex items-center gap-4 mb-3 text-xs text-slate-500 flex-wrap">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-4 h-0.5 bg-blue-600" /> UNC (conta ordinária)
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-4 border-t-2 border-dashed border-orange-600" /> FRE (fundo de reserva)
              </span>
              <span className="text-slate-400">
                · últimos {tendencia.meses.length} {tendencia.meses.length === 1 ? "mês" : "meses"} · valores mensais
              </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <TendenciaChart titulo="Receita" dados={tendencia.meses} kUnc="unc_receita" kFre="fre_receita" />
              <TendenciaChart titulo="Despesa" dados={tendencia.meses} kUnc="unc_despesa" kFre="fre_despesa" />
              <TendenciaChart titulo="Resultado (receita − despesa)" dados={tendencia.meses} kUnc="unc_resultado" kFre="fre_resultado" totalLabel="Diferença (Receita − Despesa)" />
              <TendenciaChart titulo="Saldo geral (fechamento do mês)" dados={tendencia.meses} kUnc="unc_saldo" kFre="fre_saldo" />
            </div>
          </>
        )}
      </section>

      {/* Tabela de resumos por conta */}
      <section id="sec-saldos" className="mt-8 scroll-mt-4">
        <VoltarRelatorios onVoltar={onVoltar} />
        <h3 className="text-lg font-semibold text-slate-900 mb-3">
          Saldos por conta
        </h3>
        <div className="overflow-x-auto bg-white border border-slate-200 rounded-card">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">Conta</th>
                <th className="text-right px-4 py-2.5 font-medium">
                  Saldo anterior
                </th>
                <th className="text-right px-4 py-2.5 font-medium">
                  + Receitas
                </th>
                <th className="text-right px-4 py-2.5 font-medium">
                  − Despesas
                </th>
                <th className="text-right px-4 py-2.5 font-medium">
                  Saldo final
                </th>
                <th className="text-left px-4 py-2.5 font-medium">Origem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.resumos.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-6 text-center text-slate-500"
                  >
                    Nenhum resumo encontrado.
                  </td>
                </tr>
              ) : (
                data.resumos.map((r) => {
                  // Conferência aritmética: SA + Rec - Desp deveria ser SF
                  const sa = Number(r.saldo_anterior);
                  const rec = Number(r.receitas);
                  const desp = Number(r.despesas);
                  const sf = Number(r.saldo_final);
                  const esperado = sa + rec - desp;
                  const diferenca = Math.abs(esperado - sf);
                  const okAritmetica = diferenca < 0.01;
                  return (
                    <tr key={r.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">
                          {r.conta.codigo}
                        </div>
                        <div className="text-xs text-slate-500">
                          {r.conta.nome}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {fmtBRL(r.saldo_anterior)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-emerald-700">
                        {fmtBRL(r.receitas)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-rose-700">
                        {fmtBRL(r.despesas)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">
                        <div className="flex items-center justify-end gap-1.5">
                          {okAritmetica ? (
                            <CheckCircle2
                              size={14}
                              className="text-emerald-600"
                            />
                          ) : (
                            <AlertTriangle
                              size={14}
                              className="text-amber-600"
                            />
                          )}
                          {fmtBRL(r.saldo_final)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        <ChipPagina fonte={r.fonte_pagina} onAbrir={abrirPagina} />
                        {r.confianca && (
                          <span className="ml-1 text-slate-400">
                            ({Math.round(Number(r.confianca) * 100)}%)
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {data.resumos.length > 0 && (
              <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                <tr className="font-semibold text-slate-900">
                  <td className="px-4 py-3">
                    <div>TOTAL</div>
                    <div className="text-xs text-slate-500 font-normal">
                      Saldo Geral Consolidado
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {fmtBRL(
                      data.resumos
                        .reduce(
                          (acc, r) => acc + Number(r.saldo_anterior),
                          0,
                        )
                        .toFixed(2),
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-emerald-700">
                    {fmtBRL(
                      data.resumos
                        .reduce((acc, r) => acc + Number(r.receitas), 0)
                        .toFixed(2),
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-rose-700">
                    {fmtBRL(
                      data.resumos
                        .reduce((acc, r) => acc + Number(r.despesas), 0)
                        .toFixed(2),
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {fmtBRL(
                      data.resumos
                        .reduce((acc, r) => acc + Number(r.saldo_final), 0)
                        .toFixed(2),
                    )}
                  </td>
                  <td className="px-4 py-3" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          O ícone ao lado do saldo final indica se a aritmética bate (
          <CheckCircle2 size={11} className="inline text-emerald-600" />{" "}
          saldo anterior + receitas − despesas = saldo final).
        </p>
      </section>

      {/* Cotas Condominiais e Inadimplência */}
      <div id="sec-cotas" className="scroll-mt-4 mt-8">
        <VoltarRelatorios onVoltar={onVoltar} />
        <CotasSection competenciaId={competenciaId} />
      </div>

      {/* Chamadas Extras / Fundo de Reserva — mesmos controles das cotas */}
      <div id="sec-chamadas-extras" className="scroll-mt-4 mt-8">
        <VoltarRelatorios onVoltar={onVoltar} />
        <ChamadasExtrasSection competenciaId={competenciaId} />
      </div>

      {/* Resumo de DOC's — pagina original, parseada em tabelas HTML reais
          para alinhamento correto (pre mono nao funciona com OCR de fonte
          proporcional do Vision). */}
      {data.resumo_docs_texto && (
        <div id="sec-docs" className="scroll-mt-4 mt-8">
          <VoltarRelatorios onVoltar={onVoltar} />
          <ResumoDocsSection
            texto={data.resumo_docs_texto}
            pagina={data.resumo_docs_pagina}
            competenciaId={competenciaId}
            onVerPagina={abrirPagina}
            gasAuditoria={data.gas_auditoria}
            onAbrirGasRateio={onAbrirGasRateio}
          />
        </div>
      )}

      {/* Despesas por Categoria — tabela + pizza */}
      <div id="sec-despesas" className="scroll-mt-4 mt-8">
        <VoltarRelatorios onVoltar={onVoltar} />
        <DespesasCategoriaSection
          json={data.resumo_despesas_json}
          competenciaId={competenciaId}
          onVerPagina={abrirPagina}
        />
        {/* Despesas do Fundo de Reserva (FRE) — busca própria; some/mostra zerado */}
        <DespesasFundoReservaSection
          competenciaId={competenciaId}
          onVerPagina={abrirPagina}
        />
      </div>

      {/* Documentos Fiscais — NFs + DARFs + Guias ISSQN (Fase 1) */}
      <div id="sec-fiscal" className="scroll-mt-4 mt-8">
        <VoltarRelatorios onVoltar={onVoltar} />
        <DocumentosFiscaisSection competenciaId={competenciaId} />
      </div>

      {/* Lista de findings (resultado da auditoria) */}
      <section id="sec-findings" className="mt-8 scroll-mt-4">
        <VoltarRelatorios onVoltar={onVoltar} />
        <h3 className="text-lg font-semibold text-slate-900 mb-3">
          Validações da auditoria
        </h3>
        {data.findings.length === 0 ? (
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-card text-sm text-slate-600">
            Nenhuma validação foi registrada para esta competência.
          </div>
        ) : (
          <div className="space-y-2">
            {data.findings.map((f) => (
              <FindingItem key={f.id} finding={f} onAbrirPagina={abrirPagina} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// =====================================================================
// Subcomponentes
// =====================================================================
function BadgeContagem({
  tipo,
  valor,
  label,
}: {
  tipo: "pass" | "warn" | "fail";
  valor: number;
  label: string;
}) {
  const cfg = {
    pass: {
      classe: "bg-emerald-50 text-emerald-800 border-emerald-200",
      icone: <CheckCircle2 size={14} className="text-emerald-600" />,
    },
    warn: {
      classe: "bg-amber-50 text-amber-800 border-amber-200",
      icone: <AlertTriangle size={14} className="text-amber-600" />,
    },
    fail: {
      classe: "bg-rose-50 text-rose-800 border-rose-200",
      icone: <AlertCircle size={14} className="text-rose-600" />,
    },
  }[tipo];
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border ${cfg.classe}`}
    >
      {cfg.icone}
      <span className="font-semibold tabular-nums">{valor}</span>
      <span className="text-xs">{label}</span>
    </div>
  );
}

function FindingItem({
  finding,
  onAbrirPagina,
}: {
  finding: FindingOut;
  onAbrirPagina: (pagina: number) => void;
}) {
  const cfg = {
    PASS: {
      classe: "bg-emerald-50 border-emerald-200",
      icone: <CheckCircle2 size={16} className="text-emerald-600 mt-0.5" />,
      titulo: "text-emerald-900",
    },
    WARN: {
      classe: "bg-amber-50 border-amber-200",
      icone: <AlertTriangle size={16} className="text-amber-600 mt-0.5" />,
      titulo: "text-amber-900",
    },
    FAIL: {
      classe: "bg-rose-50 border-rose-200",
      icone: <AlertCircle size={16} className="text-rose-600 mt-0.5" />,
      titulo: "text-rose-900",
    },
  }[finding.severidade];

  return (
    <div className={`p-3 border rounded-card flex gap-3 ${cfg.classe}`}>
      {cfg.icone}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className={`text-sm font-semibold ${cfg.titulo}`}>
            {finding.validacao}
          </span>
          {finding.evidencia_pagina && (
            <ChipPagina fonte={finding.evidencia_pagina} onAbrir={onAbrirPagina} />
          )}
        </div>
        <p className="text-sm text-slate-700 mt-0.5">{finding.mensagem}</p>
        {(finding.valor_esperado || finding.valor_encontrado) && (
          <div className="mt-1.5 text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-0.5">
            {finding.valor_esperado && (
              <span>
                Esperado:{" "}
                <span className="font-mono text-slate-800">
                  {finding.valor_esperado}
                </span>
              </span>
            )}
            {finding.valor_encontrado && (
              <span>
                Encontrado:{" "}
                <span className="font-mono text-slate-800">
                  {finding.valor_encontrado}
                </span>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
